import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { MobileNavigation } from "@/components/MobileNavigation";
import { DesktopNavigation } from "@/components/DesktopNavigation";
import { Input } from "@/components/ui/input";
import { Search as SearchIcon, Cake, Cookie, Candy, Carrot, Tent, UtensilsCrossed, Coffee, Droplet, Beef, Milk, Baby, Croissant, Soup, Sprout, Fish, Apple, Wheat, Nut, Pizza, Sandwich, HelpCircle, Leaf, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";

const foodGroupIcons = {
  'Baked Foods': Cake,
  'Snacks': Cookie, 
  'Sweets': Candy,
  'Vegetables': Carrot,
  'American Indian': Tent,
  'Restaurant Foods': UtensilsCrossed,
  'Beverages': Coffee,
  'Fats and Oils': Droplet,
  'Meats': Beef,
  'Dairy and Egg Products': Milk,
  'Baby Foods': Baby,
  'Breakfast Cereals': Croissant,
  'Soups and Sauces': Soup,
  'Beans and Lentils': Sprout,
  'Fish': Fish,
  'Fruits': Apple,
  'Grains and Pasta': Wheat,
  'Nuts and Seeds': Nut,
  'Prepared Meals': Pizza,
  'Fast Foods': Sandwich,
  'Spices and Herbs': Leaf
} as const;

const Search = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setDebouncedSearchTerm(searchTerm);
    }
  };

  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['nutrition-search', debouncedSearchTerm],
    queryFn: async () => {
      if (!debouncedSearchTerm) return { results: [] };
      
      const { data: embeddingData, error: embeddingError } = await supabase.functions.invoke('search-nutrition', {
        body: { searchTerm: debouncedSearchTerm },
      });

      if (embeddingError) {
        toast({
          title: "Search Error",
          description: "Failed to search nutrition facts. Please try again.",
          variant: "destructive",
        });
        throw embeddingError;
      }

      const ids = embeddingData.results.map((item: any) => item.id);
      const { data: nutritionData, error: nutritionError } = await supabase
        .from('nutrition_facts')
        .select('*')
        .in('id', ids);

      if (nutritionError) {
        toast({
          title: "Error",
          description: "Failed to fetch nutrition details. Please try again.",
          variant: "destructive",
        });
        throw nutritionError;
      }

      const results = nutritionData.map((item: any) => ({
        ...item,
        similarity: embeddingData.results.find((r: any) => r.id === item.id).similarity
      }));

      // Sort results by similarity in descending order
      results.sort((a: any, b: any) => b.similarity - a.similarity);

      return { results };
    },
    enabled: debouncedSearchTerm.length > 0,
  });

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-0">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Search</h1>
          <DesktopNavigation />
        </div>
        <div className="relative mb-6">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            type="search"
            placeholder="Search nutrition facts..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
          />
        </div>

        {isLoading ? (
          <div className="text-center text-gray-500 mt-8">
            Searching...
          </div>
        ) : searchResults?.results?.length > 0 ? (
          <div className="space-y-4">
            <ul className="divide-y divide-gray-200 bg-white rounded-lg shadow">
              {searchResults.results.map((item: any) => {
                const Icon = foodGroupIcons[item.food_group as keyof typeof foodGroupIcons] || HelpCircle;
                return (
                  <li 
                    key={item.id} 
                    className="p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/nutrition-facts/${item.id}`)}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col">
                        <span className="text-gray-900">{item.name}</span>
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Icon className="h-4 w-4" />
                          <span>{item.food_group}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <span className="text-sm">
                          {(item.similarity * 100).toFixed(1)}% match
                        </span>
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : debouncedSearchTerm ? (
          <div className="text-center text-gray-500 mt-8">
            No results found
          </div>
        ) : (
          <div className="text-center text-gray-500 mt-8">
            Enter a search term to find nutrition facts
          </div>
        )}
      </div>
      <MobileNavigation />
    </div>
  );
};

export default Search;
